'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import { ArrowLeft, Check, ExternalLink } from 'lucide-react';
import Image, { type StaticImageData } from 'next/image';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { useUrlSync } from '@/lib/hooks/useUrlSync';
import awareNotConsideredIllustration from './aware-not-considered.webp';
import noReasonNotToIllustration from './no-reason-not-to.webp';
import notAwareIllustration from './not-aware.webp';
import { STARTING_POINTS, type StartingPoint, type StartingPointData } from './path-data';
export type WhyNotBuyingStep = 'choose' | 'explore';
const VALID_STAGES: StartingPoint[] = ['not-aware', 'aware-not-considered', 'no-reason-not-to'];
const URL_SYNC_FILTERS = {
  stage: {
    paramName: 'stage',
    defaultValue: ''
  }
} as const;
export interface WhyNotBuyingProps {
  /** Controlled starting point selection. */
  initialPath?: StartingPoint;
}
export function WhyNotBuying(t0) {
  const $ = _c(18);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    initialPath
  } = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      filters: URL_SYNC_FILTERS
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    values,
    handlers
  } = useUrlSync(t2);
  const rootRef = useRef(null);
  let t3;
  if ($[3] !== initialPath || $[4] !== values.stage) {
    t3 = VALID_STAGES.includes(values.stage as StartingPoint) ? values.stage as StartingPoint : initialPath ?? null;
    $[3] = initialPath;
    $[4] = values.stage;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const selectedPath = t3;
  const step = selectedPath ? "explore" : "choose";
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function scrollToTop() {
      setTimeout(() => {
        const element = rootRef.current;
        if (element) {
          element.scrollIntoView({
            behavior: "instant",
            block: "start"
          });
        }
      }, 150);
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const scrollToTop = t4;
  let t5;
  if ($[7] !== handlers) {
    t5 = function handleSelect(id) {
      handlers.stage(id);
      scrollToTop();
    };
    $[7] = handlers;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const handleSelect = t5;
  let t6;
  if ($[9] !== handlers) {
    t6 = function handleBack() {
      handlers.stage("");
      scrollToTop();
    };
    $[9] = handlers;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const handleBack = t6;
  if (step === "choose" || !selectedPath) {
    let t7;
    if ($[11] !== handleSelect) {
      t7 = <div ref={rootRef} className="scroll-mt-20"><ChooseStep onSelect={handleSelect} /></div>;
      $[11] = handleSelect;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    return t7;
  }
  let t7;
  if ($[13] !== selectedPath) {
    t7 = STARTING_POINTS.find(sp => sp.id === selectedPath);
    $[13] = selectedPath;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const data = t7;
  if (!data) {
    return null;
  }
  let t8;
  if ($[15] !== data || $[16] !== handleBack) {
    t8 = <div ref={rootRef} className="scroll-mt-20"><ExploreStep data={data} onBack={handleBack} /></div>;
    $[15] = data;
    $[16] = handleBack;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
const STAGE_ILLUSTRATIONS: Record<StartingPoint, {
  src: StaticImageData;
  alt: string;
}> = {
  'not-aware': {
    src: notAwareIllustration,
    alt: 'A shopper passes a food product without noticing it'
  },
  'aware-not-considered': {
    src: awareNotConsideredIllustration,
    alt: 'A shopper notices a food product but keeps walking'
  },
  'no-reason-not-to': {
    src: noReasonNotToIllustration,
    alt: 'A shopper with a basket reaches for a food product'
  }
};

// ---------------------------------------------------------------------------
// Step 1: Choose your starting point
// ---------------------------------------------------------------------------

function ChooseStep(t0) {
  const $ = _c(5);
  const {
    onSelect
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div><h2 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">Why aren't people buying your product, how to change that</h2><p className="mt-3 text-base leading-relaxed text-gray-600">Where are consumers at with your product right now? Each path helps you understand what's going on and gives you concrete ideas to do something about it.</p></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== onSelect) {
    t2 = STARTING_POINTS.map(sp => {
      const illustration = STAGE_ILLUSTRATIONS[sp.id];
      return <button key={sp.id} type="button" onClick={() => onSelect(sp.id)} className={cn("group flex h-full min-w-0 cursor-pointer flex-col rounded-xl border-2 p-4 text-left transition-all sm:p-5", "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2", sp.accent.border, sp.accent.bg)}><Image src={illustration.src} alt={illustration.alt} width={1200} height={800} sizes="(min-width: 1024px) 30vw, (min-width: 640px) 46vw, 92vw" className="mb-5 aspect-3/2 w-full rounded-lg object-cover" /><div className="flex flex-1 flex-col"><h3 className={cn("text-base font-bold sm:text-lg", sp.accent.text)}>{sp.title}</h3><p className="mt-2 text-sm leading-relaxed text-gray-700">{sp.subtitle}</p></div></button>;
    });
    $[1] = onSelect;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="space-y-8">{t1}<div className="grid auto-rows-fr grid-cols-1 items-stretch gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3">{t2}</div></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

// ---------------------------------------------------------------------------
// Step 2: Explore the path — education + frictions + actions
// ---------------------------------------------------------------------------

function ExploreStep(t0) {
  const $ = _c(66);
  const {
    data,
    onBack
  } = t0;
  const Icon = data.icon;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [checkedFrictions, setCheckedFrictions] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function toggleFriction(id) {
      setCheckedFrictions(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const toggleFriction = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ArrowLeft className="h-4 w-4" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== onBack) {
    t4 = <button type="button" onClick={onBack} className="mb-4 flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900 cursor-pointer">{t3}Back to where consumers are at</button>;
    $[3] = onBack;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== data.accent.iconBg) {
    t5 = cn("flex h-12 w-12 shrink-0 items-center justify-center rounded-xl", data.accent.iconBg);
    $[5] = data.accent.iconBg;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== data.accent.text) {
    t6 = cn("h-6 w-6", data.accent.text);
    $[7] = data.accent.text;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== Icon || $[10] !== t6) {
    t7 = <Icon className={t6} />;
    $[9] = Icon;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <div className={t5}>{t7}</div>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== data.title) {
    t9 = <h2 className="text-2xl font-bold leading-tight text-gray-900 sm:text-3xl">{data.title}</h2>;
    $[15] = data.title;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== data.subtitle) {
    t10 = <p className="mt-1 text-base text-gray-600">{data.subtitle}</p>;
    $[17] = data.subtitle;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t9) {
    t11 = <div>{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t8) {
    t12 = <div className="flex items-start gap-4">{t8}{t11}</div>;
    $[22] = t11;
    $[23] = t8;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t12 || $[26] !== t4) {
    t13 = <div>{t4}{t12}</div>;
    $[25] = t12;
    $[26] = t4;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== data.accent.bg || $[29] !== data.accent.border) {
    t14 = cn("rounded-xl border-2 p-5 sm:p-6", data.accent.border, data.accent.bg);
    $[28] = data.accent.bg;
    $[29] = data.accent.border;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <p className="text-sm font-semibold uppercase tracking-wider text-gray-500">Why this matters</p>;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== data.accent.text) {
    t16 = cn("mt-2 text-[15px] leading-relaxed", data.accent.text);
    $[32] = data.accent.text;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== data.education || $[35] !== t16) {
    t17 = <p className={t16}>{data.education}</p>;
    $[34] = data.education;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== t14 || $[38] !== t17) {
    t18 = <section className={t14}>{t15}{t17}</section>;
    $[37] = t14;
    $[38] = t17;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  let t20;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <h3 className="text-lg font-bold text-gray-900">What might be going on</h3>;
    t20 = <p className="mt-1 text-sm text-gray-600">Tick the ones you think are real for your product. This is for your own clarity, not a quiz.</p>;
    $[40] = t19;
    $[41] = t20;
  } else {
    t19 = $[40];
    t20 = $[41];
  }
  let t21;
  if ($[42] !== checkedFrictions || $[43] !== data.frictions) {
    let t22;
    if ($[45] !== checkedFrictions) {
      t22 = friction => {
        const isChecked = checkedFrictions.has(friction.id);
        return <button key={friction.id} type="button" onClick={() => toggleFriction(friction.id)} className={cn("flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-all cursor-pointer", isChecked ? "border-gray-900 bg-gray-50" : "border-gray-200 bg-white hover:border-gray-300")}><div className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors", isChecked ? "border-gray-900 bg-gray-900" : "border-gray-300 bg-white")}>{isChecked && <Check className="h-3.5 w-3.5 text-white" />}</div><div><p className="text-sm font-medium text-gray-900">{friction.title}</p><p className="mt-0.5 text-sm italic text-gray-500">“{friction.customerThought}”</p></div></button>;
      };
      $[45] = checkedFrictions;
      $[46] = t22;
    } else {
      t22 = $[46];
    }
    t21 = data.frictions.map(t22);
    $[42] = checkedFrictions;
    $[43] = data.frictions;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  let t22;
  if ($[47] !== t21) {
    t22 = <section>{t19}{t20}<div className="mt-4 space-y-3">{t21}</div></section>;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  let t24;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <h3 className="text-lg font-bold text-gray-900">How to change this</h3>;
    t24 = <p className="mt-1 text-sm text-gray-600">Concrete things you could try. Not all will be right for you — pick what makes sense for your product, stage, and budget.</p>;
    $[49] = t23;
    $[50] = t24;
  } else {
    t23 = $[49];
    t24 = $[50];
  }
  let t25;
  if ($[51] !== data.actions) {
    t25 = data.actions.map(_temp);
    $[51] = data.actions;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t25) {
    t26 = <section>{t23}{t24}<div className="mt-5 space-y-4">{t25}</div></section>;
    $[53] = t25;
    $[54] = t26;
  } else {
    t26 = $[54];
  }
  let t27;
  let t28;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <p className="text-sm font-semibold text-gray-900">What next?</p>;
    t28 = <p className="mt-2 text-sm leading-relaxed text-gray-600">Pick one or two actions that feel most doable right now. Small experiments that get your product in front of more of the right people, or give people a reason to try it. Track what happens. Come back and try another path if your situation changes.</p>;
    $[55] = t27;
    $[56] = t28;
  } else {
    t27 = $[55];
    t28 = $[56];
  }
  let t29;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ArrowLeft className="h-4 w-4" />;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  let t30;
  if ($[58] !== onBack) {
    t30 = <section className="rounded-xl border border-gray-200 bg-gray-50 p-5 sm:p-6">{t27}{t28}<button type="button" onClick={onBack} className="mt-4 flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800 cursor-pointer">{t29}Explore where else consumers might be at</button></section>;
    $[58] = onBack;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== t13 || $[61] !== t18 || $[62] !== t22 || $[63] !== t26 || $[64] !== t30) {
    t31 = <div className="mx-auto max-w-3xl space-y-10">{t13}{t18}{t22}{t26}{t30}</div>;
    $[60] = t13;
    $[61] = t18;
    $[62] = t22;
    $[63] = t26;
    $[64] = t30;
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  return t31;
}
function _temp(action) {
  return <div key={action.id} className="rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300"><div className="flex items-start justify-between gap-3"><div><div className="flex items-center gap-2"><h4 className="text-sm font-semibold text-gray-900">{action.title}</h4><span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide", action.category === "online" && "bg-violet-50 text-violet-700", action.category === "irl" && "bg-orange-50 text-orange-700", action.category === "both" && "bg-gray-100 text-gray-600")}>{action.category === "irl" ? "IRL" : action.category}</span></div><p className="mt-1 text-sm leading-relaxed text-gray-600">{action.description}</p></div>{action.href && <Link href={action.href} className="shrink-0 rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 cursor-pointer" title="Explore on Sizzle"><ExternalLink className="h-4 w-4" /></Link>}</div></div>;
}